import React, { useEffect, useMemo, useState } from "react";
import {
  ClipboardDocumentIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import { apiTokensService } from "../../services/apiTokens";
import { getExternalApiBaseUrl } from "../../services/externalApi";
import { getApiErrorMessage } from "../../utils/axios";

const CLIENTS = [
  {
    id: "claude",
    label: "Claude",
    hint: "Run this in your terminal. Claude Code saves it to ~/.claude.json.",
  },
  {
    id: "cursor",
    label: "Cursor",
    hint: "Paste into ~/.cursor/mcp.json, or .cursor/mcp.json in your project.",
  },
  {
    id: "windsurf",
    label: "Windsurf",
    hint: "Paste into ~/.codeium/windsurf/mcp_config.json.",
  },
  {
    id: "codex",
    label: "Codex",
    hint: "Run this in your terminal. Codex saves it to ~/.codex/config.toml.",
  },
  {
    id: "muse",
    label: "Muse",
    hint: "Paste into ~/.config/muse/settings.json.",
  },
  {
    id: "openclaw",
    label: "OpenClaw",
    hint: "Paste into ~/.openclaw/openclaw.json under mcp.servers.",
  },
  {
    id: "grok",
    label: "Grok Bot",
    hint: "In a Bot chat, ask Grok to add this MCP server URL with your Authorization header.",
  },
  {
    id: "other",
    label: "Other",
    hint: "Use these values in any MCP client that supports Streamable HTTP.",
  },
];

const SKILL_PATH = "/agent/SKILL.md";

const AgentInstructionsModal = ({ onClose }) => {
  const [client, setClient] = useState("claude");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [agentKey, setAgentKey] = useState(null);
  const [keyVisible, setKeyVisible] = useState(false);
  const [copied, setCopied] = useState(null);
  const [skillText, setSkillText] = useState("");

  const mcpUrl = `${getExternalApiBaseUrl()}/mcp/`;
  const keyForSnippets = agentKey || "YOUR_API_KEY";

  useEffect(() => {
    fetch(SKILL_PATH)
      .then((r) => r.text())
      .then(setSkillText)
      .catch(() => setSkillText(""));
  }, []);

  const snippets = useMemo(
    () => buildClientSnippets(client, mcpUrl, keyForSnippets),
    [client, mcpUrl, keyForSnippets],
  );

  const copyText = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    } catch (_) {}
  };

  const createAgentKey = () => {
    setCreating(true);
    setCreateError(null);
    apiTokensService.createToken(
      { name: "Agent" },
      (res) => {
        setCreating(false);
        setAgentKey(res.data?.token || null);
        setKeyVisible(true);
        // Keys/Playground stay mounted under the modal; tell them to reload.
        window.dispatchEvent(new CustomEvent("weave-api-tokens-changed"));
      },
      (err) => {
        setCreating(false);
        setCreateError(getApiErrorMessage(err, "Could not create API key"));
      },
    );
  };

  const downloadSkill = () => {
    const blob = new Blob([skillText || ""], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "SKILL.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-6 pt-6 pb-4 border-b border-theme-secondary shrink-0">
        <h2 className="text-xl font-semibold text-theme-text">Connect Weave to your agent</h2>
        <p className="mt-1 text-sm text-theme-text opacity-70">
          Give your coding agent one connection to create sites, request changes, and publish.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-8">
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-theme-text">Your API key</h3>
          {agentKey ? (
            <div className="rounded-lg border border-theme-secondary bg-theme-secondary bg-opacity-30 px-4 py-3 flex items-center gap-3">
              <code className="flex-1 text-sm font-mono text-theme-primary break-all">
                {keyVisible ? agentKey : maskKey(agentKey)}
              </code>
              <button
                type="button"
                onClick={() => setKeyVisible((v) => !v)}
                className="text-theme-text opacity-70 hover:opacity-100"
                aria-label={keyVisible ? "Hide key" : "Show key"}
              >
                {keyVisible ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
              <button
                type="button"
                onClick={() => copyText(agentKey, "key")}
                className="text-theme-text opacity-70 hover:opacity-100"
                aria-label="Copy key"
              >
                <ClipboardDocumentIcon className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <button
                type="button"
                onClick={createAgentKey}
                disabled={creating}
                className="rounded-lg bg-theme-primary px-4 py-2 text-sm font-semibold text-theme-button disabled:opacity-50"
                style={{ backgroundColor: "var(--theme-primary)", color: "var(--theme-button-text)" }}
              >
                {creating ? "Creating…" : "Create Agent API Key"}
              </button>
              {createError && <p className="text-sm text-red-400">{createError}</p>}
            </div>
          )}
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-theme-text">Connect via MCP</h3>
          <div className="inline-flex flex-wrap gap-1 rounded-xl border border-theme-secondary bg-theme-secondary bg-opacity-30 p-1">
            {CLIENTS.map((c) => {
              const selected = client === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setClient(c.id)}
                  title={c.label}
                  aria-label={c.label}
                  aria-pressed={selected}
                  className={`h-8 w-8 rounded-lg flex items-center justify-center transition-opacity ${
                    selected
                      ? "bg-theme-main opacity-100 ring-2 ring-theme-primary"
                      : "opacity-70 hover:opacity-100"
                  }`}
                >
                  <img src={`/agent/icons/${c.id}.svg`} alt="" className="h-5 w-5" />
                </button>
              );
            })}
          </div>
          <CodeBlock
            lines={snippets.lines}
            copied={copied === "mcp"}
            onCopy={() => copyText(snippets.text, "mcp")}
          />
          <p className="text-xs text-theme-text opacity-60">
            {CLIENTS.find((c) => c.id === client)?.hint}
          </p>
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-theme-text">Ask your agent to connect</h3>
          <div className="rounded-lg border border-theme-secondary bg-theme-secondary bg-opacity-30 p-4 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-theme-text font-mono">SKILL.md</div>
              <div className="text-xs text-theme-text opacity-60 mt-0.5">
                Weave agent skill, paste into your AI&apos;s context.
              </div>
            </div>
            <button
              type="button"
              onClick={() => copyText(skillText, "skill")}
              className="inline-flex items-center gap-1.5 text-xs text-theme-text opacity-80 hover:opacity-100"
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
              {copied === "skill" ? "Copied" : "Copy"}
            </button>
            <button
              type="button"
              onClick={downloadSkill}
              className="inline-flex items-center gap-1.5 text-xs text-theme-text opacity-80 hover:opacity-100"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Download
            </button>
          </div>
        </section>
      </div>

      <div className="px-6 py-4 border-t border-theme-secondary flex justify-end shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg bg-theme-primary px-5 py-2 text-sm font-semibold text-theme-button"
          style={{ backgroundColor: "var(--theme-primary)", color: "var(--theme-button-text)" }}
        >
          Done
        </button>
      </div>
    </div>
  );
};

function CodeBlock({ lines, copied, onCopy }) {
  return (
    <div className="relative rounded-lg border border-theme-secondary bg-theme-secondary bg-opacity-30 overflow-hidden">
      <button
        type="button"
        onClick={onCopy}
        className="absolute top-2 right-2 inline-flex items-center gap-1 text-xs text-theme-text opacity-70 hover:opacity-100"
      >
        <ClipboardDocumentIcon className="h-4 w-4" />
        {copied ? "Copied" : "Copy"}
      </button>
      <pre className="p-4 pr-20 text-xs font-mono text-theme-text overflow-x-auto whitespace-pre-wrap">
        {lines.map((line, i) => (
          <div key={i}>
            <span className="opacity-40 select-none mr-3">{i + 1}</span>
            {line}
          </div>
        ))}
      </pre>
    </div>
  );
}

function buildClientSnippets(client, mcpUrl, apiKey) {
  if (client === "claude") {
    const lines = [
      `claude mcp add --transport http weave ${mcpUrl} --header "Authorization: Bearer ${apiKey}"`,
    ];
    return { lines, text: lines.join("\n") };
  }
  if (client === "cursor") {
    const config = {
      mcpServers: {
        weave: {
          url: mcpUrl,
          headers: {
            Authorization: `Bearer ${apiKey}`,
          },
        },
      },
    };
    const text = JSON.stringify(config, null, 2);
    return { lines: text.split("\n"), text };
  }
  if (client === "windsurf") {
    const config = {
      mcpServers: {
        weave: {
          serverUrl: mcpUrl,
          headers: {
            Authorization: `Bearer ${apiKey}`,
          },
        },
      },
    };
    const text = JSON.stringify(config, null, 2);
    return { lines: text.split("\n"), text };
  }
  if (client === "codex") {
    const lines = [
      `export WEAVE_API_KEY=${apiKey}`,
      `codex mcp add weave --url ${mcpUrl} --bearer-token-env-var WEAVE_API_KEY`,
    ];
    return { lines, text: lines.join("\n") };
  }
  if (client === "muse") {
    const config = {
      schema_version: 1,
      mcp_servers: {
        weave: {
          transport: "streamable_http",
          url: mcpUrl,
          headers: {
            Authorization: `Bearer ${apiKey}`,
          },
          mode: "optional",
        },
      },
    };
    const text = JSON.stringify(config, null, 2);
    return { lines: text.split("\n"), text };
  }
  if (client === "openclaw") {
    const config = {
      mcp: {
        servers: {
          weave: {
            url: mcpUrl,
            transport: "streamable-http",
            headers: {
              Authorization: `Bearer ${apiKey}`,
            },
          },
        },
      },
    };
    const text = JSON.stringify(config, null, 2);
    return { lines: text.split("\n"), text };
  }
  if (client === "grok") {
    const lines = [
      `grok mcp add --transport http weave ${mcpUrl} --header "Authorization: Bearer ${apiKey}"`,
      `# Or in Grok Bot: Settings → Plugins → Add MCP server`,
      `# URL: ${mcpUrl}`,
      `# Header: Authorization: Bearer ${apiKey}`,
    ];
    return { lines, text: lines.join("\n") };
  }
  const lines = [
    `MCP URL: ${mcpUrl}`,
    `Header: Authorization: Bearer ${apiKey}`,
  ];
  return { lines, text: lines.join("\n") };
}

function maskKey(key) {
  if (!key || key.length < 12) return "wve_********";
  return `${key.slice(0, 12)}********`;
}

export default AgentInstructionsModal;
